import { SIN_TABLE } from './trig-table.ts';

/** Integer helpers. Everything in sim is an integer (SPEC §3). */

/** Truncated integer division. */
export const idiv = (a: number, b: number): number => Math.trunc(a / b);

/** Floor division (rounds toward −∞). */
export const fdiv = (a: number, b: number): number => {
  const q = Math.trunc(a / b);
  return q * b > a ? q - 1 : q;
};

/** Round-half-up integer division for non-negative operands. */
export const rdiv = (a: number, b: number): number => Math.trunc((a * 2 + b) / (b * 2));

export const imin = (a: number, b: number): number => (a < b ? a : b);
export const imax = (a: number, b: number): number => (a > b ? a : b);
export const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/** Multiply by basis points (10000 = ×1), truncating. */
export const mulBp = (v: number, bp: number): number => Math.trunc((v * bp) / 10000);
/** Multiply by basis points, rounding half up (non-negative). */
export const mulBpRound = (v: number, bp: number): number => rdiv(v * bp, 10000);

/** Integer square root (floor). */
export const isqrt = (n: number): number => {
  if (n <= 0) return 0;
  if (n < 4) return 1;
  // Newton from a power of two at or above √n (half the bit length, rounded up): same floor(√n),
  // a handful of steps instead of ~log2(n).
  const hi = Math.trunc(n / 4294967296);
  const bits = hi > 0 ? 64 - Math.clz32(hi) : 32 - Math.clz32(n);
  let x = 1 << ((bits + 1) >> 1);
  let y = Math.trunc((x + Math.trunc(n / x)) / 2);
  while (y < x) {
    x = y;
    y = Math.trunc((x + Math.trunc(n / x)) / 2);
  }
  return x;
};

/** sqrt of a value expressed in bp, result in bp. sqrtBp(40000) = 20000. */
export const sqrtBp = (v: number): number => isqrt(v * 10000);
/** v^0.75 in bp. */
export const pow075Bp = (v: number): number => {
  const s = sqrtBp(v);
  return Math.trunc((s * sqrtBp(s)) / 10000);
};

/** Euclidean length of an integer vector. */
export const len = (dx: number, dy: number): number => isqrt(dx * dx + dy * dy);
export const dist2 = (ax: number, ay: number, bx: number, by: number): number => {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
};
export const withinRange = (ax: number, ay: number, bx: number, by: number, r: number): boolean =>
  dist2(ax, ay, bx, by) <= r * r;

/** Angles are integers in [0, 1024). sin/cos scaled by 10000. */
export const ANGLE_STEPS = 1024;
export const sinA = (a: number): number => SIN_TABLE[((a % ANGLE_STEPS) + ANGLE_STEPS) % ANGLE_STEPS] as number;
export const cosA = (a: number): number => sinA(a + 256);

/** Integer atan2 → angle in [0, 1024). Octant approximation, good to ~1 step. */
export const iatan2 = (dy: number, dx: number): number => {
  if (dx === 0 && dy === 0) return 0;
  const ax = Math.abs(dx);
  const ay = Math.abs(dy);
  // angle in first octant: atan(m) ≈ 128·m·(1 + 0.2732(1−m)) / (π/4 → 128 steps), m = min/max in [0,1]
  const mn = ax < ay ? ax : ay;
  const mx = ax < ay ? ay : ax;
  const m = Math.trunc((mn * 10000) / mx); // bp
  // atan(m)/(2π)·1024 with atan(m) ≈ (π/4)m + 0.273·m(1−m)
  const a = Math.trunc((m * 1280000 + m * (10000 - m) * 44 + 50000000) / 100000000);
  let ang = ax >= ay ? a : 256 - a;
  if (dx < 0) ang = 512 - ang;
  if (dy < 0) ang = 1024 - ang;
  return ((ang % 1024) + 1024) % 1024;
};
